/** RTK Query endpoints for odl_app site reads and writes. */
import { entitiesApi } from '../entitiesApi';

import { ingestSiteSnapshot } from './ingest';
import { setFeatureAssociations } from './sliceMeta';
import { markUnitSnapshotRequestStarted } from './sliceUnit';

const siteApi = entitiesApi.injectEndpoints({
  endpoints: build => ({
    /**
     * Fetch the composite site read.
     *
     * GET /api/odl-app/sites/{id}/ → { data: { site, unit, alarms,
     * command_activity, media_summary, feature_associations },
     * meta: { permissions } }. The
     * transform flattens both halves into one cache entry and seeds a null
     * `alarmsGeneratedAt` sibling for the socket monotonic guard.
     *
     * serializeQueryArgs canonicalizes the id to a Number so string/number
     * callers and the status selector resolve to the same cache entry.
     *
     * @param {{ id: number|string }} arg
     */
    retrieveSite: build.query({
      query: ({ id }) => ({ url: `sites/${id}/` }),
      serializeQueryArgs: ({ queryArgs }) => ({ id: Number(queryArgs.id) }),
      transformResponse: body => ({
        site: body?.data?.site ?? null,
        unit: body?.data?.unit ?? null,
        alarms: body?.data?.alarms ?? [],
        command_activity: body?.data?.command_activity ?? null,
        media_summary: body?.data?.media_summary ?? null,
        feature_associations: body?.data?.feature_associations ?? [],
        _permissions: body?.meta?.permissions ?? null,
        // Sibling monotonic-guard timestamp for the alarms socket patch; the
        // alarms array itself stays a plain serialisable array.
        alarmsGeneratedAt: null,
      }),
      async onQueryStarted({ id }, { dispatch, getState, queryFulfilled }) {
        const siteId = Number(id);
        const requestVersion = (getState().siteUnit?.latestSnapshotRequestVersionBySiteId?.[String(siteId)] ?? 0) + 1;
        dispatch(markUnitSnapshotRequestStarted({ siteId, requestVersion }));
        try {
          const { data } = await queryFulfilled;
          // Fan the composite payload out into the site entity's normalized
          // stores (site / unit / alarms / command activity / meta / media rail).
          dispatch(ingestSiteSnapshot(siteId, data, { requestVersion }));
        } catch {
          // Load failure is reflected in the query error state; nothing to fan out.
        }
      },
      providesTags: (result, error, { id }) => [{ type: 'Site', id: Number(id) }],
    }),

    /**
     * Toggle a site feature association and patch the meta store in place.
     *
     * PATCH /api/odl-app/sites/{siteId}/feature-associations/ with
     * { feature_key, is_enabled, is_billable?, default_enabled_for_children? }.
     * Returns the full updated feature_associations list, which onQueryStarted
     * writes into the meta store so the WEATHER section gate stays in sync
     * without a full re-fetch.
     *
     * @param {{ siteId: number|string, feature_key: string, is_enabled: boolean,
     *           is_billable?: boolean, default_enabled_for_children?: boolean }} arg
     */
    upsertSiteFeatureAssociation: build.mutation({
      query: ({ siteId, ...body }) => ({
        url: `sites/${siteId}/feature-associations/`,
        method: 'patch',
        data: body,
      }),
      transformResponse: b => b.data ?? b,
      async onQueryStarted({ siteId }, { dispatch, queryFulfilled }) {
        try {
          const { data: featureAssociations } = await queryFulfilled;
          dispatch(setFeatureAssociations({ siteId: Number(siteId), featureAssociations }));
        } catch {
          // Toggle failed server-side; leave the store untouched.
        }
      },
    }),

    /**
     * Create a site. Body is SiteSerializer input (same shape as the legacy
     * POST /sites/). Returns the grid read shape (plain JS, 201).
     *
     * @param {{ body: object }} arg
     */
    createSite: build.mutation({
      query: ({ body }) => ({ url: 'sites/', method: 'post', data: body }),
      transformResponse: b => b.data,
      invalidatesTags: [{ type: 'Site', id: 'LIST' }],
    }),

    /**
     * Preflight bulk site deactivation — either an explicit id list or a
     * select-all over the current grid filters. Returns affected-site and
     * attached-unit/radio counts.
     *
     * @param {{ siteIds?: Array<number>, selectAll?: boolean, filters?: object }} arg
     *        `filters` is the grid's JSON:API param dict (filter[<field>] keys),
     *        used only when `selectAll` is set.
     */
    preflightDeactivateSites: build.mutation({
      query: ({ siteIds, selectAll, filters }) => ({
        url: 'sites/bulk/preflight/',
        method: 'post',
        data: selectAll ? { select_all: true, filters } : { site_ids: siteIds },
      }),
      transformResponse: b => b.data ?? b,
    }),

    /**
     * Bulk-deactivate sites — either an explicit id list or a select-all over
     * the current grid filters. Returns
     * { deactivated, already_deactivated, not_found }.
     *
     * @param {{ siteIds?: Array<number>, selectAll?: boolean, filters?: object,
     *           radioAction?: 'inventory'|'purge' }} arg
     *        `filters` is the grid's JSON:API param dict (filter[<field>] keys),
     *        used only when `selectAll` is set.
     */
    deactivateSites: build.mutation({
      query: ({ siteIds, selectAll, filters, radioAction }) => ({
        url: 'sites/bulk/',
        method: 'post',
        data: selectAll
          ? { action: 'deactivate', select_all: true, filters, radio_action: radioAction }
          : { action: 'deactivate', site_ids: siteIds, radio_action: radioAction },
      }),
      transformResponse: b => b.data ?? b,
      invalidatesTags: [{ type: 'Site', id: 'LIST' }],
    }),

    /**
     * Bulk-transfer sites to a target site group — explicit ids or select-all.
     * Returns { transferred, target_site_group }.
     *
     * @param {{ siteIds?: Array<number>, selectAll?: boolean, filters?: object,
     *           targetSiteGroupId: number }} arg
     */
    transferSites: build.mutation({
      query: ({ siteIds, selectAll, filters, targetSiteGroupId }) => ({
        url: 'sites/bulk/',
        method: 'post',
        data: selectAll
          ? {
              action: 'transfer',
              select_all: true,
              filters,
              target_site_group_id: targetSiteGroupId,
            }
          : {
              action: 'transfer',
              site_ids: siteIds,
              target_site_group_id: targetSiteGroupId,
            },
      }),
      transformResponse: b => b.data ?? b,
      invalidatesTags: [{ type: 'Site', id: 'LIST' }],
    }),

    /**
     * Bulk-reactivate deactivated sites. Returns SiteSerializer (many).
     *
     * @param {{ siteIds: Array<number> }} arg
     */
    reactivateSites: build.mutation({
      query: ({ siteIds }) => ({
        url: 'sites/bulk-activate/',
        method: 'post',
        data: { site_ids: siteIds },
      }),
      transformResponse: b => b.data ?? b,
      invalidatesTags: [{ type: 'Site', id: 'LIST' }],
    }),
  }),
});

export { siteApi };
export const {
  useRetrieveSiteQuery,
  useLazyRetrieveSiteQuery,
  useUpsertSiteFeatureAssociationMutation,
  useCreateSiteMutation,
  usePreflightDeactivateSitesMutation,
  useDeactivateSitesMutation,
  useTransferSitesMutation,
  useReactivateSitesMutation,
} = siteApi;
